// Riel de navegación vertical (solo iconos, con ayudas emergentes a la derecha). Reemplaza la barra superior:
// arriba aplicaciones, búsqueda y Crear; en medio las secciones de Preventa; abajo notificaciones, ayuda y cuenta.
import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { v2 } from '../api/clienteV2';
import { crm } from '../api/comercial';
import { equipo } from '../api/equipoComercial';
import { useSesion } from '../auth/contexto';
import { usaMock } from '../config';
import { DialogoFirmas } from '../components/FirmaCorreo';
import { Icono, type NombreIcono } from '../components/Iconos';
import { Avatar, ItemMenu, Kbd, Popover, SeparadorMenu } from '../components/Ui';
import { useTodosLosCasos } from '../hooks/useCasos';
import { ETIQUETA_ROL } from '../lib/etiquetas';
import { administraUsuarios, esPreventa, puedeGestionarCompras, puedeGestionarPersonas, puedeRedactarTextos, puedeVerPersonas, puedeOperar, puedeVerCompras, puedeVerCRM, puedeVerIndicadores, veInteligenciaCompras, vePreciosProveedor } from '../lib/permisos';
import { useUi } from './contexto';
import { Notificaciones } from './Notificaciones';

const APPS: { clave: string; nombre: string; desc: string; icono: NombreIcono; a?: string }[] = [
  { clave: 'comercial', nombre: 'Comercial', desc: 'Cuentas, oportunidades y pipeline de ventas', icono: 'diana', a: '/comercial' },
  { clave: 'preventa', nombre: 'Preventa', desc: 'Diseño de solución, costeo y ofertas', icono: 'tablero', a: '/preventa/tablero' },
  { clave: 'proyectos', nombre: 'Proyectos', desc: 'Ejecución y entregas', icono: 'tipoProyecto' },
  { clave: 'compras', nombre: 'Compras', desc: 'Abastecimiento, proveedores e inteligencia de precios', icono: 'tipoSuministro', a: '/compras' },
  { clave: 'personas', nombre: 'Personas', desc: 'Gestión Humana y Vida Laboral', icono: 'usuarios', a: '/personas' },
  { clave: 'mesa-de-ayuda', nombre: 'Mesa de ayuda', desc: 'Soporte y casos de servicio', icono: 'tipoServicio' },
  { clave: 'facturación', nombre: 'Facturación', desc: 'Facturas y cartera', icono: 'dinero' },
];

interface Enlace {
  a: string;
  texto: string;
  icono: NombreIcono;
  contador?: number;
  alerta?: boolean;
  oculto?: boolean;
}

export function Riel() {
  const sesion = useSesion();
  const ui = useUi();
  const { pathname } = useLocation();
  const [firmasAbierto, setFirmas] = useState(false);
  const app = pathname.startsWith('/comercial') ? 'comercial' : pathname.startsWith('/compras') ? 'compras' : pathname.startsWith('/personas') ? 'personas' : 'preventa';
  const operar = puedeOperar(sesion.roles);
  const todos = useTodosLosCasos();
  const excepciones = useQuery({ queryKey: ['excepciones', 'ABIERTA'], queryFn: () => api.listarExcepciones('ABIERTA'), staleTime: 60_000 });
  const solicitudes = useQuery({ queryKey: ['solicitudes', 'abiertas'], queryFn: () => api.solicitudes('nueva'), staleTime: 60_000, enabled: operar, retry: false });
  const porAprobar = (todos.data?.items ?? []).filter((c) => c.estado === 'POR_APROBAR').length;
  const bandeja = useQuery({ queryKey: ['bandeja'], queryFn: v2.bandeja, staleTime: 60_000, enabled: operar, retry: false });
  const procesos = useQuery({ queryKey: ['procesos-publicos'], queryFn: () => v2.procesos(), staleTime: 5 * 60_000, retry: false });
  const porDecidir = (bandeja.data?.secciones ?? []).filter((x) => ['borradores_por_enviar', 'decisiones_requeridas', 'sin_vincular', 'nuevas_solicitudes'].includes(x.clave)).reduce((t, x) => t + x.total, 0);
  const porGestionar = (bandeja.data?.secciones ?? []).find((x) => x.clave === 'gestiones')?.total ?? 0;
  const crmPendientes = useQuery({ queryKey: ['crm', 'correos-pendientes', false], queryFn: () => crm.correosPendientes(false), staleTime: 60_000, enabled: app === 'comercial' && puedeVerCRM(sesion.roles), retry: false });
  const crmAprob = useQuery({ queryKey: ['crm', 'aprobaciones', 'pendiente'], queryFn: () => equipo.aprobaciones({ estado: 'pendiente' }), staleTime: 60_000, enabled: app === 'comercial' && puedeVerCRM(sesion.roles), retry: false });
  const alertasProcesos = (procesos.data ?? []).reduce((t, p) => t + p.alertas.length, 0);

  const gruposComercial: Enlace[][] = [
    [
      { a: '/comercial', texto: 'Inicio comercial', icono: 'inicio' },
      { a: '/comercial/equipo', texto: 'Mi equipo digital', icono: 'usuarios' },
      { a: '/comercial/oportunidades', texto: 'Oportunidades', icono: 'diana' },
      { a: '/comercial/cuentas', texto: 'Cuentas', icono: 'edificio' },
      { a: '/comercial/prospectos', texto: 'Prospectos', icono: 'usuario' },
      { a: '/comercial/forecast', texto: 'Forecast y metas', icono: 'indicadores' },
    ],
    [
      { a: '/comercial/correos', texto: 'Correos por vincular', icono: 'bandeja', contador: crmPendientes.data?.length, alerta: (crmPendientes.data?.length ?? 0) > 0 },
      { a: '/comercial/aprobaciones', texto: 'Aprobaciones comerciales', icono: 'aprobaciones', contador: crmAprob.data?.length },
      { a: '/comercial/compras', texto: 'Solicitudes a Compras', icono: 'carpeta' },
    ],
    [
      { a: '/comercial/tableros', texto: 'Tableros de gerencia', icono: 'tablero' },
      { a: '/comercial/competencia', texto: 'Competencia y win/loss', icono: 'diana' },
    ],
  ];
  const gruposPreventa: Enlace[][] = [
    [
      { a: '/preventa/bandeja', texto: 'Bandeja de Orion', icono: 'buzon', contador: porDecidir, oculto: !operar },
      { a: '/preventa/tablero', texto: 'Pipeline de preventa', icono: 'tablero' },
      { a: '/preventa/lista', texto: 'Lista de casos', icono: 'lista' },
      { a: '/preventa/mi-trabajo', texto: 'Mi trabajo', icono: 'miTrabajo' },
      { a: '/preventa/solicitudes', texto: 'Solicitudes de preventa', icono: 'bandeja', contador: solicitudes.data?.length },
    ],
    [
      { a: '/preventa/aprobaciones', texto: 'Aprobaciones', icono: 'aprobaciones', contador: porAprobar, oculto: !operar },
      { a: '/preventa/excepciones', texto: 'Excepciones', icono: 'excepciones', contador: excepciones.data?.length, alerta: (excepciones.data ?? []).some((e) => e.vencida) },
    ],
    [
      { a: '/preventa/contratacion', texto: 'Contratación pública', icono: 'tipoLicitacion', contador: alertasProcesos, alerta: alertasProcesos > 0 },
      { a: '/preventa/gestiones', texto: 'Gestiones de preventa', icono: 'maletin', contador: porGestionar || undefined, oculto: !operar },
      { a: '/preventa/proveedores', texto: 'Proveedores', icono: 'edificio' },
    ],
    [
      { a: '/preventa/brechas', texto: 'Reporte de brechas', icono: 'brecha' },
      { a: '/preventa/biblioteca', texto: 'Biblioteca', icono: 'libro' },
      { a: '/preventa/equipo', texto: 'Equipo', icono: 'usuarios', oculto: !operar },
      { a: '/preventa/indicadores', texto: 'Indicadores', icono: 'indicadores', oculto: !puedeVerIndicadores(sesion.roles) },
      { a: '/preventa/catalogos', texto: 'Catálogos', icono: 'catalogos' },
    ],
  ];

  const gestionaCompras = puedeGestionarCompras(sesion.roles);
  const gruposCompras: Enlace[][] = [
    [
      { a: '/compras', texto: 'Tablero de Compras', icono: 'indicadores', oculto: !veInteligenciaCompras(sesion.roles) },
      { a: '/compras/bandeja', texto: 'Bandeja de Compras', icono: 'buzon', oculto: !gestionaCompras },
      { a: '/compras/solicitudes', texto: 'Solicitudes', icono: 'bandeja' },
    ],
    [
      { a: '/compras/proveedores', texto: 'Proveedores y homologación', icono: 'edificio', oculto: !gestionaCompras },
      { a: '/compras/cotizaciones', texto: 'Cotizaciones', icono: 'archivo', oculto: !vePreciosProveedor(sesion.roles) },
      { a: '/compras/inteligencia', texto: 'Inteligencia de precios', icono: 'tendenciaSube', oculto: !veInteligenciaCompras(sesion.roles) },
    ],
  ];
  const gestionaPersonas = puedeGestionarPersonas(sesion.roles);
  const gruposPersonas: Enlace[][] = [
    [
      { a: '/personas', texto: 'Inicio de Gestión Humana', icono: 'inicio', oculto: !gestionaPersonas },
      { a: '/personas/bandeja', texto: 'Bandeja de Gestión Humana', icono: 'bandeja', oculto: !gestionaPersonas },
      { a: '/personas/colaboradores', texto: 'Colaboradores', icono: 'usuarios', oculto: !gestionaPersonas },
      { a: '/personas/seleccion', texto: 'Selección', icono: 'diana', oculto: !gestionaPersonas },
      { a: '/personas/ingresos', texto: 'Ingresos', icono: 'enviar', oculto: !gestionaPersonas },
      { a: '/personas/contratos', texto: 'Contratos', icono: 'archivo', oculto: !gestionaPersonas && !puedeRedactarTextos(sesion.roles) },
    ],
    [
      { a: '/personas/organizacion', texto: 'Organigrama y plazas', icono: 'diagrama' },
      { a: '/personas/cargos', texto: 'Cargos y perfiles', icono: 'maletin' },
      { a: '/personas/directorio', texto: 'Directorio', icono: 'libro' },
    ],
    [
      { a: '/personas/textos-legales', texto: 'Textos legales', icono: 'firma', oculto: !puedeRedactarTextos(sesion.roles) },
      { a: '/personas/requisitos', texto: 'Requisitos de ingreso', icono: 'lista', oculto: !gestionaPersonas },
      { a: '/personas/usuarios', texto: 'Usuarios y permisos', icono: 'usuarios', oculto: !administraUsuarios(sesion.roles) },
    ],
    [{ a: '/personas/mi', texto: 'Mi Vida Laboral', icono: 'usuario' }],
  ];
  const grupos = app === 'comercial' ? gruposComercial : app === 'compras' ? gruposCompras : app === 'personas' ? gruposPersonas : gruposPreventa;
  // Quien solo tiene roles de Personas (un colaborador) no ve Preventa.
  const soloPersonas = sesion.roles.length > 0 && sesion.roles.every((r) => r.startsWith('Personas.'));
  const apps = APPS.filter(
    (a) =>
      (a.clave !== 'comercial' || puedeVerCRM(sesion.roles)) &&
      (a.clave !== 'compras' || puedeVerCompras(sesion.roles)) &&
      (a.clave !== 'personas' || puedeVerPersonas(sesion.roles)) &&
      (a.clave !== 'preventa' || !soloPersonas),
  );

  return (
    <nav className="riel" aria-label={`Navegación de UPK INNOVA ${app === 'comercial' ? 'Comercial' : app === 'compras' ? 'Compras' : app === 'personas' ? 'Personas' : 'Preventa'}`}>
      <div className="riel__arriba">
        <Popover
          ubicacion="derecha-inicio"
          ancho={320}
          etiqueta="Aplicaciones de UPK INNOVA"
          disparador={({ ref, alternar, props, abierto }) => (
            <button ref={ref} type="button" className={`riel__marca ${abierto ? 'riel__marca--abierta' : ''}`} onClick={alternar} aria-label="UPK INNOVA, cambiar de aplicación" data-ayuda="UPK INNOVA" data-ayuda-pos="derecha" {...props}>
              <img src={`${import.meta.env.BASE_URL}logo-upk-marca.png`} alt="" width={40} height={19} />
            </button>
          )}
        >
          {(cerrar) => (
            <div className="apps">
            <p className="apps__titulo">UPK INNOVA</p>
            <ul className="apps__rejilla">
              {apps.map((a) => (
                <li key={a.nombre}>
                  {a.a ? (
                    <Link to={a.a} className={`app-item ${a.clave === app ? 'app-item--activa' : ''}`} aria-current={a.clave === app ? 'true' : undefined} onClick={cerrar}>
                      <span className="app-item__icono">
                        <Icono nombre={a.icono} tam={20} />
                      </span>
                      <span className="app-item__texto">
                        <strong>{a.nombre}</strong>
                        <small>{a.desc}</small>
                      </span>
                    </Link>
                  ) : (
                    <span className="app-item app-item--futura" aria-disabled="true">
                      <span className="app-item__icono">
                        <Icono nombre={a.icono} tam={20} />
                      </span>
                      <span className="app-item__texto">
                        <strong>{a.nombre}</strong>
                        <small>Próximamente</small>
                      </span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
            </div>
          )}
        </Popover>
        <button type="button" className="riel__boton" onClick={ui.abrirPaleta} aria-label="Buscar (Ctrl+K)" aria-keyshortcuts="Control+K /" data-ayuda="Buscar · Ctrl K" data-ayuda-pos="derecha">
          <Icono nombre="buscar" tam={20} />
        </button>
        {app === 'preventa' && esPreventa(sesion.roles) && (
          <button type="button" className="riel__crear" onClick={ui.abrirCrear} aria-label="Crear caso (C)" aria-keyshortcuts="C" data-ayuda="Crear caso · C" data-ayuda-pos="derecha">
            <Icono nombre="mas" tam={20} />
          </button>
        )}
      </div>

      <div className="riel__medio">
        {grupos.map((g, i) => (
          <ul key={i} role="list" className="riel__grupo">
            {g
              .filter((e) => !e.oculto)
              .map((e) => (
                <li key={e.a}>
                  <NavLink to={e.a} end={e.a === '/comercial' || e.a === '/compras' || e.a === '/personas'} className={({ isActive }) => `riel__boton ${isActive ? 'riel__boton--activo' : ''}`} data-ayuda={e.texto} data-ayuda-pos="derecha" aria-label={`${e.texto}${e.contador ? `, ${e.contador} pendientes` : ''}`}>
                    <Icono nombre={e.icono} tam={20} />
                    {!!e.contador && <span className={`riel__contador ${e.alerta ? 'riel__contador--alerta' : ''}`}>{e.contador > 9 ? '9+' : e.contador}</span>}
                  </NavLink>
                </li>
              ))}
          </ul>
        ))}
      </div>

      <div className="riel__abajo">
        {usaMock && (
          <span className="riel__entorno" title="Datos de demostración">
            Demo
          </span>
        )}
        <Notificaciones enRiel />
        <Popover
          ubicacion="derecha-fin"
          ancho={240}
          rol="menu"
          etiqueta="Ayuda"
          disparador={({ ref, alternar, props, abierto }) => (
            <button ref={ref} type="button" className={`riel__boton ${abierto ? 'riel__boton--abierto' : ''}`} onClick={alternar} aria-label="Ayuda" data-ayuda="Ayuda" data-ayuda-pos="derecha" {...props}>
              <Icono nombre="ayuda" tam={20} />
            </button>
          )}
        >
          {(cerrar) => (
            <div className="menu" role="menu" aria-label="Ayuda">
              <ItemMenu icono={<Icono nombre="teclado" />} extra={<Kbd>?</Kbd>} onClick={() => (cerrar(), ui.abrirAtajos())}>
                Atajos de teclado
              </ItemMenu>
              <ItemMenu icono={<Icono nombre="buscar" />} extra={<Kbd>/</Kbd>} onClick={() => (cerrar(), ui.abrirPaleta())}>
                Buscar en Preventa
              </ItemMenu>
              <SeparadorMenu />
              <p className="menu__nota">UPK INNOVA Colombia · UP KEEP SERVICES S.A.S.</p>
            </div>
          )}
        </Popover>
        <Popover
          ubicacion="derecha-fin"
          ancho={300}
          rol="menu"
          etiqueta="Cuenta"
          disparador={({ ref, alternar, props }) => (
            <button ref={ref} type="button" className="riel__avatar btn-avatar" onClick={alternar} aria-label={`Cuenta de ${sesion.usuario.nombre}`} data-ayuda={sesion.usuario.nombre} data-ayuda-pos="derecha" {...props}>
              <Avatar oid={sesion.usuario.oid} nombre={sesion.usuario.nombre} tam={32} titulo={false} />
            </button>
          )}
        >
          {(cerrar) => (
            <div className="cuenta">
              <div className="cuenta__cabecera">
                <Avatar oid={sesion.usuario.oid} nombre={sesion.usuario.nombre} tam={40} titulo={false} />
                <div>
                  <strong>{sesion.usuario.nombre}</strong>
                  {sesion.usuario.correo && <small>{sesion.usuario.correo}</small>}
                </div>
              </div>
              <div className="cuenta__roles">
                <span className="menu__grupo">Roles</span>
                <div className="cuenta__chips">
                  {sesion.roles.length ? sesion.roles.map((r) => <span key={r} className="chip-rol">{ETIQUETA_ROL[r] ?? r}</span>) : <span className="texto-tenue">Sin roles</span>}
                </div>
              </div>
              <div className="menu" role="menu" aria-label="Cuenta">
                {sesion.usuariosDev && sesion.cambiarUsuarioDev && (
                  <>
                    <SeparadorMenu titulo="Usuario de prueba" />
                    <div className="cuenta__dev">
                      {sesion.usuariosDev.map((u) => (
                        <ItemMenu
                          key={u.oid}
                          rol="menuitemradio"
                          seleccionado={u.oid === sesion.usuario.oid}
                          icono={<Avatar oid={u.oid} nombre={u.nombre} tam={20} titulo={false} />}
                          ayuda={u.roles.map((r) => ETIQUETA_ROL[r]).join(', ')}
                          onClick={() => {
                            cerrar();
                            sesion.cambiarUsuarioDev?.(u.oid);
                          }}
                        >
                          {u.nombre}
                        </ItemMenu>
                      ))}
                    </div>
                  </>
                )}
                <SeparadorMenu />
                <ItemMenu
                  icono={<Icono nombre="firma" />}
                  onClick={() => {
                    cerrar();
                    setFirmas(true);
                  }}
                >
                  Firma de los correos
                </ItemMenu>
                <ItemMenu icono={<Icono nombre="salir" />} onClick={sesion.cerrarSesion}>
                  Cerrar sesión
                </ItemMenu>
              </div>
              <VersionSistema />
            </div>
          )}
        </Popover>
      </div>
      {firmasAbierto && <DialogoFirmas alCerrar={() => setFirmas(false)} />}
    </nav>
  );
}

/** Versión de la web y del servidor. Si difieren, el despliegue quedó a medias y se muestra para soporte. */
function VersionSistema() {
  const q = useQuery({ queryKey: ['salud-version'], queryFn: () => api.salud(), staleTime: 5 * 60_000, retry: false });
  const servidor = q.data?.version;
  return (
    <div className="cuenta__version texto-tenue pequeno">
      UPK INNOVA Colombia v{__UPK_VERSION__}
      {servidor && servidor !== __UPK_VERSION__ && <span className="texto-alerta"> · servidor v{servidor}</span>}
    </div>
  );
}
